/**
 * SiteLocationsBatchCard — interactive verification card for `propose_site_locations` (PLT-5467,
 * Phase 2 of the site-location input tool — parent PLT-5319).
 *
 * The chat-only tool `propose_site_locations` (`mcp/tools/propose-site-locations.ts`) validates a
 * structured payload (the physical sites the agent parsed from the user's upload / pasted data, or
 * discovered via `identify_company_sites`) and echoes it back as the tool result. This card renders
 * that payload in the site-locations input's own table (`SiteLocationsTable`, in the wizard's
 * `review-step` layout: one line per site, a scrolling box with a pinned header, an "Edit table"
 * mode with add, delete and per-row geocoding), so a list of fifty sites reads the way it does on
 * the page. The user corrects, adds or deletes rows, resolves missing coordinates, then commits —
 * either saving a draft, or saving and publishing (which queues a variant recompute).
 *
 * The commit does NOT go back through the LLM — the card POSTs the reviewed sites directly to the
 * Phase 1 site-locations endpoints (save draft, then publish), so the commit is deterministic and
 * audit-clean. This mirrors `TailoringBatchCard`'s direct-POST design.
 *
 * variantId: like the tailoring card, the proposal payload carries `companyId` but not `variantId`.
 * The tool-UI wrapper (`upright-chat-tool-uis.tsx`) resolves the company's default variant via
 * `useGetCompanyVariantId(companyId)` and passes it in. When no variant is available (loading / no
 * access) the card disables editing and both commit actions and shows an explanatory hint.
 *
 * Commit gating splits by action, mirroring what each endpoint actually enforces.
 *
 * Saving a draft is permissive: the rows have to pass the page's own `validateRows` (a site type on
 * every row, both coordinates or neither, no negative weight), and that is all. A row without
 * coordinates saves and is simply left out of the climate pathway until it has them, so it is
 * flagged rather than blocked — blocking would strand a user whose discovered set has fifty rows
 * and eight resolvable addresses. The table's per-row geocode action (reusing
 * `useGeocodeSiteLocation`) and manual entry are both offered; neither is required.
 *
 * Publishing additionally has to satisfy the weight rules the publish endpoint enforces: within a
 * value-chain position, either every site carries a weight or none does, and a position may not
 * total more than 100%. Those are checked here with the same shared helpers the endpoint and the
 * input page use, so the user reads the reason in the card instead of a 400 from the server.
 *
 * `addressCountry` arrives as free text the tool steers toward `DMA_COUNTRIES`. A value not on that
 * list is flagged in a hint that names the sites, not blocked, matching what the tool schema tells
 * the agent; the table's country picker offers only listed values, so picking one there clears it.
 *
 * mode (PLT-5468): both modes first load the company's saved set (the page's GET hook), and the
 * editor opens once that settles. `replace` commits exactly the proposed rows and says beforehand how
 * many saved sites that overwrites. `build-on-top` matches proposed rows to saved sites once, by
 * `existingSiteId` first and a unique name+address key as fallback (`site-locations-merge.ts`).
 * Its table is the whole set that will be saved: the proposed rows first, then every saved site no
 * row claims, each labelled Edited, New or Unchanged. Deleting a row removes that site from the
 * set, which is the only way a saved site leaves it: a proposal cannot remove one.
 *
 * The proposal is ephemeral: dismissing discards it (nothing server-side until commit).
 *
 * firstApproval (PLT-6999): the card is also the site review of a custom company whose sites were
 * identified at creation, between the approval of its products and its first results. The company
 * has no variant then, so the card reads and saves the company's draft through the company-scoped
 * routes the creation wizard uses, and waits while the identification is still running. It has one
 * action, "Looks good, compute results": it saves any change and then sends the chat that message,
 * as the product set card's approve button does, and the agent publishes the products with these
 * sites. Approving unchanged sites saves nothing. Publishing is the first results' job, so the
 * publish weight rules gate the approval. Only the review approves: the card the agent marks `review`
 * when the products' approval opens it (PLT-7302), carrying any sites the user named before that, or
 * one with no proposed sites. A proposal made while the products still await approval ("add these two
 * sites") saves to the same draft and approves nothing.
 *
 * Reviewing again (PLT-7368): a save leaves every other open card on the set it replaced, and the
 * card saves without the agent hearing of it, so nothing in the chat follows up. A review whose
 * saved set changed under it, and a proposal saved after the review opened, therefore offer
 * "Review sites again" themselves: it sends the products card's "Looks good, review sites.", and the
 * agent answers with a fresh review of the saved set.
 *
 * `<SiteLocationsBatchCard>` is the sole export, mirroring `tailoring-batch-card.tsx`; sub-components
 * are private to this module, and the matching rules live in `site-locations-merge.ts` so they can
 * be unit-tested.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAssistantRuntime, useThread } from '@assistant-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { savedSetChangedSince } from '@upright/common/model/input/companies/geolocation/saved-set';
import { type SiteCoordinateLookupSummary } from '@upright/common/model/input/companies/geolocation/api-types';
import { geocodeVerdictOf } from '@upright/common/model/input/companies/geolocation/coordinate-check';
import { nameFirstFew } from '@upright/common/model/input/companies/geolocation/geocode-match';
import { ErrorWithHttpStatus } from '@upright/ui/api/common/error-with-http-status';
import {
  type CompanyGeolocationSite,
  type CompanyGeolocationSiteValues,
  type GeolocationSiteSource,
  type GeolocationSupplyChainPosition,
} from '@upright/common/model/input/companies/geolocation';
import {
  describeSiteWeightCoverage,
  exceedsFullCoverage,
  isPartlyWeighted,
} from '@upright/common/model/input/companies/geolocation/site-weights';
import { DMA_COUNTRIES } from '@upright/common/model/input/companies/operational-data/dma-countries';
import { FormControlButtons } from '@upright/ui/common/generic/components/operational-data-input/shared/form-control-buttons';
import { useEditingMode } from '@upright/ui/common/generic/components/operational-data-input/shared/use-editing-mode';
import { usePushGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event';
import { CustomGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event/payloads';
import { JobStatus } from '@upright/common/internal/jobs/types';
import { getIsJobComplete, useFetchJobStatus } from '@upright/ui/api/internal/jobs/use-fetch-job-status';
import {
  useCompanySiteDraft,
  useGeocodeCompanySite,
  useGeocodeSiteLocation,
  usePublishSiteLocations,
  useSaveCompanySiteDraft,
  useSaveSiteLocations,
  useSiteIdentificationStatus,
  useSiteLocations,
} from '@upright/ui/views/dma-v2-view/dashboard/site-locations-input/hooks';
import { SiteLocationsTable } from '@upright/ui/views/dma-v2-view/dashboard/site-locations-input/site-locations-table';
import {
  emptySiteRow,
  rowHasCoordinates,
  rowToInput,
  siteToRow,
  type SiteRow,
} from '@upright/ui/views/dma-v2-view/dashboard/site-locations-input/types';
import {
  overWeightedMessages,
  partlyWeightedMessage,
  validateRows,
} from '@upright/ui/views/dma-v2-view/dashboard/site-locations-input/validation';
import { ChatMarkdown } from './upright-chat-markdown';
import { Badge } from './components';
import { type ProductSetCardStanding } from './product-set-card-standing';
import { FOLLOW_THREAD_EVENT } from './upright-chat-ui-common';
import {
  mergeProposalWithExisting,
  proposalAsReplacement,
  type MergedProposedSite,
  type ProposedSite,
  type UnmatchedSiteReason,
} from './site-locations-merge';

// ---------------------------------------------------------------------------
// Types — the shape the chat-only tool echoes back. The per-row shape lives in
// `site-locations-merge.ts` beside the matching rules that read it.
// ---------------------------------------------------------------------------

export type SiteLocationsBatchMode = 'replace' | 'build-on-top';

export interface SiteLocationsBatchCardProps {
  offerFinancialStatements?: boolean;
  /** The company Save writes to: the one the server checked edit access against. */
  companyId: string;
  /** That company's name, resolved server-side and shown in the header; the id stands in without it. */
  companyName?: string;
  /**
   * The variant the chat is bound to. Resolved client-side from `companyId` via
   * `useGetCompanyVariantId` in the tool-UI wrapper (same mechanism as the tailoring card). When
   * null (loading / no access) the card disables commit and shows a hint.
   */
  variantId: string | null;
  proposalId: string;
  mode: SiteLocationsBatchMode;
  /**
   * Provenance of this proposal batch (PLT-5470): how the agent obtained the sites — an uploaded
   * file, the conversation itself, or site discovery. Stamped on every new row's `source` when
   * committed so the platform can distinguish agentic input from the manual page. Edited and kept
   * saved sites keep their own source.
   */
  source: ProposedSiteLocationsSource;
  summaryMarkdown?: string;
  sites: ProposedSite[];
  /**
   * The ids of the saved set the server read when the proposal was made, or null when that read
   * failed. The card refuses to save once the saved set no longer has these ids — see
   * `SiteLocationsProposalBaseline` in `mcp/tools/propose-site-locations.ts` for why the baseline
   * has to come from the tool result rather than from the card's first load.
   */
  savedSiteIdsAtProposal?: number[] | null;
  /**
   * PLT-6999: present when the company is a custom company awaiting its first approval (see the module
   * comment), with where the card stands in its thread and whether a review for the company comes
   * before it there. Absent otherwise.
   *
   * PLT-7453: with the sites the approval recorded, once a review is approved and its publish says
   * which. Absent before that, and for a publish from before publishes recorded them.
   */
  firstApproval?: {
    standing: ProductSetCardStanding;
    reviewOpenedBefore?: boolean;
    approvedSites?: CompanyGeolocationSiteValues[];
  };
  /** PLT-7302: the agent marked this card as that company's site review; see the module comment. */
  review?: boolean;
  /**
   * PLT-7300: present for a custom company past its first approval, with whether this user may update
   * its assessment with the sites (see `SiteLocationsProposalBaseline`). Absent for other companies.
   */
  customCompanyRecalculation?: { allowed: boolean };
  /**
   * PLT-7633: present when the server looked the proposed sites' coordinates up from their addresses
   * before the card rendered (sites read from a file), with what it found. `sites` already carries the
   * coordinates it identified.
   */
  coordinateLookup?: SiteCoordinateLookupSummary;
  /**
   * PLT-7416: the card was rebuilt from the stored conversation when the chat was opened again,
   * rather than drawn as the agent answered.
   */
  replayed?: boolean;
}

/** The agentic-only subset of source values a proposal can carry (never `manual`). */
export type ProposedSiteLocationsSource = Exclude<GeolocationSiteSource, 'manual'>;

// ---------------------------------------------------------------------------
// Table row — a site of the set the card will save, in the page's own row shape
// (`SiteRow`, so the page's table, mapper and validation all apply unchanged),
// plus which saved site it is. `source` is the row's provenance as the save will
// record it: a proposed new row takes the proposal's, an edited or kept saved
// site keeps its own, and a row the user adds in the table carries none, which
// the server records as `manual`.
// ---------------------------------------------------------------------------

interface CardRow extends SiteRow {
  /** The saved site this row is — edited by the proposal, or kept (build-on-top) — or null for a new site. */
  matchedExistingId: number | null;
  /** Why a row that named a saved site is saved as new anyway, or null — see the merge. */
  unmatchedReason: UnmatchedSiteReason | null;
}

type RowErrors = Partial<Record<string, Set<keyof SiteRow>>>;

/** A module constant, so the editing hook's `addRow` keeps one identity across renders. */
const EMPTY_CARD_ROW: CardRow = { ...emptySiteRow(), matchedExistingId: null, unmatchedReason: null };

/** Blank strings collapse to null, so a field the agent sent empty saves as absent, as on the page. */
function orNull(value: string | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
}

function proposedToRow(site: MergedProposedSite, proposalSource: ProposedSiteLocationsSource): CardRow {
  return {
    key: site.rowId,
    _uiId: site.rowId,
    name: orNull(site.name),
    description: orNull(site.description),
    streetAddress: orNull(site.streetAddress),
    addressLocality: orNull(site.addressLocality),
    addressRegion: orNull(site.addressRegion),
    postalCode: orNull(site.postalCode),
    addressCountry: orNull(site.addressCountry),
    locationType: site.locationType,
    // The tool schema validates the position against `allSupplyChainPositions` before it echoes it.
    supplyChainPosition: site.supplyChainPosition as GeolocationSupplyChainPosition,
    latitude: site.latitude ?? null,
    longitude: site.longitude ?? null,
    // The server's lookup verdict for a new site, the saved one for a kept pair (see the merge).
    geocodePrecision: site.geocodePrecision ?? null,
    geocodePartialMatch: site.geocodePartialMatch ?? null,
    weight: site.weight ?? null,
    evidenceUrl: orNull(site.evidenceUrl),
    // A new row takes the proposal's provenance (PLT-5470); an edit keeps the source of the site it
    // edits, because the column says where a row came from, not who last touched it.
    source: site.existingSource ?? proposalSource,
    matchedExistingId: site.matchedExistingId,
    unmatchedReason: site.unmatchedReason,
  };
}

/** A saved site no proposed row claims. `siteToRow` carries its saved `source`, so it keeps its provenance. */
function keptToRow(site: CompanyGeolocationSite): CardRow {
  return { ...siteToRow(site), _uiId: `saved-${site.id}`, matchedExistingId: site.id, unmatchedReason: null };
}

// ---------------------------------------------------------------------------
// Row status (build-on-top). Derived from the row and the saved site it is, so
// it follows the user's edits: a kept site the user changes becomes Edited, and
// a proposed edit that turns out identical to the saved site reads Unchanged.
// ---------------------------------------------------------------------------

type RowStatus = 'edited' | 'unchanged' | 'new' | UnmatchedSiteReason;

const ROW_STATUS_BADGES: Record<RowStatus, { label: string; tone: 'neutral' | 'negative'; explanation?: string }> = {
  edited: { label: 'Edited', tone: 'neutral' },
  unchanged: { label: 'Unchanged', tone: 'neutral' },
  new: { label: 'New', tone: 'neutral' },
  // Negative because these rows probably duplicate the site they meant to edit, and a plain "New"
  // hides that.
  'not-found': {
    label: 'New: saved site not found',
    tone: 'negative',
    explanation: 'This row named a saved site that no longer exists, so it is added as a new site.',
  },
  'already-edited': {
    label: 'New: duplicate edit',
    tone: 'negative',
    explanation: 'Another row already edits the saved site this row named, so it is added as a new site.',
  },
};

const COMPARED_FIELDS = [
  'name',
  'description',
  'streetAddress',
  'addressLocality',
  'addressRegion',
  'postalCode',
  'addressCountry',
  'locationType',
  'supplyChainPosition',
  'latitude',
  'longitude',
  'weight',
  'evidenceUrl',
] as const satisfies ReadonlyArray<keyof SiteRow & keyof CompanyGeolocationSite>;

function rowStatus(row: CardRow, savedById: ReadonlyMap<number, CompanyGeolocationSite>): RowStatus {
  if (row.matchedExistingId === null) return row.unmatchedReason ?? 'new';
  const saved = savedById.get(row.matchedExistingId);
  if (!saved) return 'edited';
  // A cleared text cell may come back as '' rather than null; both mean "no value".
  const norm = (value: string | number | null | undefined) =>
    typeof value === 'string' && value.trim() === '' ? null : (value ?? null);
  return COMPARED_FIELDS.every(field => norm(row[field]) === norm(saved[field])) ? 'unchanged' : 'edited';
}

const DMA_COUNTRY_SET = new Set<string>(DMA_COUNTRIES);

/** An empty country is fine; a non-empty one has to be on the platform's list to count as recognised. */
function countryIsRecognised(row: SiteRow): boolean {
  const country = row.addressCountry?.trim() ?? '';
  return country === '' || DMA_COUNTRY_SET.has(country);
}

/** "Acme Plant (Untied States)" for the first few sites with an unrecognised country, then how many more. */
function describeUnrecognisedCountries(rows: SiteRow[]): string {
  return nameFirstFew(
    rows.map(row => `${row.name?.trim() || 'an unnamed site'} (${row.addressCountry?.trim() ?? ''})`),
  );
}

/**
 * What the coordinate lookup before the card found, or null when there is nothing to say. The sites it
 * could not place need no sentence of their own: they are among the sites the missing-coordinates hint
 * counts.
 */
function describeCoordinateLookup({
  identified,
  approximate,
  unavailable,
}: SiteCoordinateLookupSummary): string | null {
  const sentences: string[] = [];
  if (identified > 0) {
    sentences.push(
      `Coordinates for ${identified === 1 ? '1 site were' : `${identified} sites were`} identified from the addresses.`,
    );
  }
  if (approximate.length > 0) {
    sentences.push(
      `${approximate.length === 1 ? 'One is an approximate match' : `${approximate.length} are approximate matches`}` +
        `, so check ${approximate.length === 1 ? 'it' : 'them'}: ${nameFirstFew(approximate)}.`,
    );
  }
  if (unavailable) {
    sentences.push('Some sites were not looked up because the geocoding service was slow or could not be reached.');
  }
  return sentences.length > 0 ? sentences.join(' ') : null;
}

// Whether a value is safe to render as a link: an absolute http(s) URL, never `javascript:` or the like.
function isHttpUrl(value: string): boolean {
  try {
    const { protocol } = new URL(value.trim());
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * The weight problems that would make `publish` fail, in the publish endpoint's own words.
 *
 * Judged over the coordinate-bearing rows only, because that is the set compute weighs — a
 * coordinate-less site takes no share of its position, so its blank weight is not a gap. This
 * mirrors `assertWeightsAreUsablePerPosition` server-side and the input page's validation, via
 * the same shared helpers, so the three cannot drift into disagreeing about the same set.
 *
 * Takes the WHOLE committed set, kept-existing rows included: publish validates the draft it is
 * given, so checking only the proposed rows would pass a card whose commit then fails.
 */
function weightProblems(rows: SiteRow[]): string[] {
  const coverages = describeSiteWeightCoverage(
    rows
      .filter(rowHasCoordinates)
      .map(row => ({ storedWeight: row.weight, supplyChainPosition: row.supplyChainPosition })),
  );
  const partly = coverages.filter(isPartlyWeighted);
  const over = coverages.filter(exceedsFullCoverage);
  // The input page's own wording, not a paraphrase of it: the same weight mistake should read the
  // same whether the user hits it in chat or on the page.
  return [...(partly.length > 0 ? [partlyWeightedMessage(partly)] : []), ...overWeightedMessages(over)];
}

// ---------------------------------------------------------------------------
// Commit state machine — editable proposal → committing → committed / failed.
// Two commit actions share the machine; `committed.published` records which was
// run so the receipt reads "saved" vs "saved and assessment updating".
//
// `publish-failed` is its own state because the save before it landed: the draft
// exists, and showing the editable table again with a bare error would read as
// "nothing happened" and invite a second save that only rewrites the draft.
// ---------------------------------------------------------------------------

type CommitState =
  | { phase: 'idle' }
  | { phase: 'committing'; published: boolean }
  // `recomputeJobId` is what publish queued; the receipt polls it so "updating" can become "updated".
  | { phase: 'committed'; published: boolean; recomputeJobId: number | null }
  | { phase: 'failed'; message: string }
  | { phase: 'publish-failed'; message: string; retrying: boolean }
  | { phase: 'dismissed' };

/** What the editor knows about the company's saved site set when it opens. */
type ExistingSetState =
  /** No variant to read it through; the card cannot save either. */
  { status: 'unavailable' } | { status: 'failed' } | { status: 'loaded'; sites: CompanyGeolocationSite[] };

interface SiteLocationsFunnelData {
  companyId: string;
  mode: SiteLocationsBatchMode;
  source: ProposedSiteLocationsSource;
  siteCount: number;
}

// ---------------------------------------------------------------------------
// Top-level component.
// ---------------------------------------------------------------------------

export function SiteLocationsBatchCard({
  offerFinancialStatements,
  companyId,
  variantId,
  // `proposalId` distinguishes re-renders of the same proposal and is part of the echoed payload.
  // The save/publish endpoints do not take it — they key the draft by companyId — so the card holds
  // it without sending it.
  proposalId: _proposalId,
  companyName,
  mode,
  // Stamped on every new row at commit (PLT-5470). Proposals never come from the manual page, so
  // they are never 'manual'.
  source: proposalSource,
  summaryMarkdown,
  sites,
  savedSiteIdsAtProposal,
  firstApproval,
  review,
  customCompanyRecalculation,
  coordinateLookup,
  replayed = false,
}: SiteLocationsBatchCardProps) {
  const isFirstApproval = firstApproval !== undefined;
  // The review the products' approval opens approves; a proposal made while the products still await
  // approval only saves (see the editor).
  const isReview = isFirstApproval && (review === true || sites.length === 0);
  // A card leads back to the review only while no newer review follows it: once one does, that one is
  // the way on. A stale review and a proposal saved after it can both offer it; both send the same.
  const canReviewAgain =
    isFirstApproval && firstApproval.standing === 'current' && (isReview || firstApproval.reviewOpenedBefore === true);
  // The card names whose sites it saves (DESIGN.md, "An MCP card names its own subject"): a chat can
  // be about a company and its suppliers at once, and Save writes to exactly one of them. The name is
  // the server's for `companyId`, never the agent's; the id stands in when it could not be resolved,
  // as that rule prescribes.
  const subject = companyName?.trim() || companyId;

  // Funnel telemetry (PLT-5470): entered/proposed -> edited -> saved/published. Uses the platform's
  // standard GTM mechanism (`usePushGtmEvent`); GTM is no-op'd in dev/non-prod and for team users, so
  // these only fire for real customer usage. The site count carried is the proposed-row count.
  const pushGtmEvent = usePushGtmEvent();
  const funnelData = useMemo<SiteLocationsFunnelData>(
    () => ({ companyId, mode, source: proposalSource, siteCount: sites.length }),
    [companyId, mode, proposalSource, sites.length],
  );
  // "Entered + proposed": fire once when the card first mounts with a rendered proposal. A card
  // rebuilt when the chat is opened again is the same proposal, already counted.
  useEffect(() => {
    if (replayed) return;
    pushGtmEvent({ event: CustomGtmEvent.PLG_SITE_LOCATIONS_PROPOSED, data: funnelData });
    // Fire once per card instance; funnelData is stable for a given proposal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Before the first approval the sites may still be being identified when the review opens; the
  // table waits for them. A status that cannot be read answers `none`, which opens it.
  const { status: identification } = useSiteIdentificationStatus(isFirstApproval ? companyId : undefined);
  const waitingForSites = isFirstApproval && (identification === undefined || identification.status === 'running');

  // Both modes read the saved set: build-on-top merges onto it, and replace has to say what it is
  // about to overwrite. Reusing the page's GET hooks keeps one source of truth for the set: the
  // variant's working set once a variant is resolved, or, before the first approval, the company's
  // draft (else its newest published set), once the identification has saved it.
  const variantQuery = useSiteLocations(companyId, isFirstApproval ? null : variantId);
  const draftQuery = useCompanySiteDraft(isFirstApproval && !waitingForSites ? companyId : undefined);
  const savedSet = isFirstApproval
    ? {
        isFetched: draftQuery.isFetched,
        isSuccess: draftQuery.isSuccess,
        sites:
          draftQuery.data === undefined
            ? undefined
            : ((draftQuery.data.draft ?? draftQuery.data.published)?.sites ?? []),
      }
    : {
        isFetched: variantQuery.isFetched,
        isSuccess: variantQuery.isSuccess,
        sites: variantQuery.data === undefined ? undefined : (variantQuery.data?.sites ?? []),
      };

  // A review the user has moved past says so in place of the table, which would otherwise come back
  // with a working button whenever the chat is reopened.
  if (firstApproval?.standing === 'approved') {
    if (isReview) {
      return (
        <ApprovedSitesReceipt companyId={companyId} subject={subject} approvedSites={firstApproval.approvedSites} />
      );
    }
    return (
      <section className="site-locations-batch-card" aria-label="Proposed site locations">
        <div className="tailoring-batch-card-hint">
          The products and sites for {subject} were approved after this proposal was made. Ask for a fresh proposal to
          change its sites.
        </div>
      </section>
    );
  }
  if (isReview && firstApproval.standing === 'replaced') {
    return (
      <section className="site-locations-batch-card" aria-label="Site locations to review">
        <div className="tailoring-batch-card-hint">Replaced by the review below.</div>
      </section>
    );
  }
  if (waitingForSites) {
    return (
      <section
        className="site-locations-batch-card"
        aria-label={isReview ? 'Site locations to review' : 'Proposed site locations'}
      >
        <SiteLocationsBatchHeader
          subject={subject}
          siteCount={null}
          mode={mode}
          savedSiteCount={null}
          review={isReview}
        />
        <div className="tailoring-batch-card-hint" aria-live="polite">
          Still identifying sites for {subject} from public sources. This can take a few minutes; the table opens here
          when it is done.
        </div>
      </section>
    );
  }
  // The loading view is only for the first load of the saved set. Once the editor has opened it
  // must never be swapped out again: it holds the user's edits and, mid-commit, the commit itself.
  // Gate on `isFetched`, not `isLoading`: react-query returns a query that failed with no data to
  // `pending` when it refetches, so `isLoading` turns back on, and unmounting then lost a commit that
  // went on to save and publish without its receipt. `isFetched` stays true once a load has settled.
  if ((isFirstApproval || variantId !== null) && !savedSet.isFetched) {
    return (
      <section className="site-locations-batch-card" aria-label="Proposed site locations">
        {/* No count yet: in build-on-top the table also holds the saved sites still loading, so the
            proposal's own row count (none at all, for a removal) would say the wrong number. */}
        <SiteLocationsBatchHeader
          subject={subject}
          siteCount={null}
          mode={mode}
          savedSiteCount={null}
          review={isReview}
        />
        <div className="tailoring-batch-card-hint">Checking the sites already saved for this company…</div>
      </section>
    );
  }
  const existing: ExistingSetState =
    !isFirstApproval && variantId === null
      ? { status: 'unavailable' }
      : savedSet.isSuccess
        ? { status: 'loaded', sites: savedSet.sites ?? [] }
        : { status: 'failed' };
  // The ids as of the latest successful load. The query refetches whenever a save in this tab
  // invalidates it, so this is how an open card learns, before anyone presses Save, that another
  // card saved under it. A save from the page, the wizard or another tab does not reach this query;
  // the save endpoint refuses those (see `expectedSiteIds` in the commit).
  const latestSavedSiteIds = savedSet.sites === undefined ? null : savedSet.sites.map(site => site.id);

  // Keyed on the variant: `useGetCompanyVariantId` cannot tell "still resolving" from "none", so the
  // editor may first open without one. When the variant arrives the editor reopens on the saved set
  // it then loads, rather than keeping rows merged against nothing. A first approval needs none.
  return (
    <SiteLocationsBatchEditor
      offerFinancialStatements={offerFinancialStatements}
      key={isFirstApproval ? 'first-approval' : (variantId ?? 'no-variant')}
      firstApproval={isFirstApproval}
      review={isReview}
      canReviewAgain={canReviewAgain}
      companyId={companyId}
      subject={subject}
      variantId={variantId}
      mode={mode}
      proposalSource={proposalSource}
      summaryMarkdown={summaryMarkdown}
      sites={sites}
      existing={existing}
      savedSiteIdsAtProposal={savedSiteIdsAtProposal ?? null}
      latestSavedSiteIds={latestSavedSiteIds}
      funnelData={funnelData}
      customCompanyRecalculation={customCompanyRecalculation}
      coordinateLookup={coordinateLookup}
      replayed={replayed}
    />
  );
}

function SiteLocationsBatchEditor({
  offerFinancialStatements,
  firstApproval,
  review,
  canReviewAgain,
  companyId,
  subject,
  variantId,
  mode,
  proposalSource,
  summaryMarkdown,
  sites,
  existing,
  savedSiteIdsAtProposal,
  latestSavedSiteIds,
  funnelData,
  customCompanyRecalculation,
  coordinateLookup,
  replayed,
}: {
  offerFinancialStatements?: boolean;
  /** Before a custom company's first results, when the card saves to its draft; see the module comment. */
  firstApproval: boolean;
  /** That company's site review, which approves the sites with the products. */
  review: boolean;
  /** Whether the card, once its saved set changes, offers "Review sites again"; see the module comment. */
  canReviewAgain: boolean;
  companyId: string;
  /** The company's name, or its id when no name was resolved. */
  subject: string;
  variantId: string | null;
  mode: SiteLocationsBatchMode;
  proposalSource: ProposedSiteLocationsSource;
  summaryMarkdown?: string;
  sites: ProposedSite[];
  /** The saved set as the editor opens on it. Read once; later loads arrive as `latestSavedSiteIds`. */
  existing: ExistingSetState;
  savedSiteIdsAtProposal: number[] | null;
  latestSavedSiteIds: number[] | null;
  funnelData: SiteLocationsFunnelData;
  customCompanyRecalculation?: { allowed: boolean };
  /** The lookup that filled the proposed sites' coordinates; see the card's props. */
  coordinateLookup?: SiteCoordinateLookupSummary;
  /** Rebuilt from the stored conversation; see the card's props. */
  replayed: boolean;
}) {
  // Everything about the saved set is fixed at the moment the editor opens: which rows edit which
  // saved sites, which sites are kept, and the ids that later loads are compared with. None of it is
  // re-derived from the rows as the user edits them — a match that moved with every keystroke would
  // let a saved site drop out of the commit, or come back as a duplicate, while someone typed a
  // street name — nor from later loads, which are what the comparison is for.
  //
  // The ids compared with are those the proposal was made against, not those the editor opened on.
  // The two agree for a fresh card. They differ for a card mounted again after a save — the chat
  // view remounts its cards from the stored tool result, with this commit state gone — and there the
  // set it opens on already includes that save, its own included, so judging from it would offer the
  // old proposal for saving a second time. The editor's own first load is the fallback for a
  // proposal whose baseline the server could not read.
  const [opened] = useState(() => {
    const loadedSites = existing.status === 'loaded' ? existing.sites : null;
    const buildsOnSavedSet = mode === 'build-on-top' && loadedSites !== null;
    const merge = buildsOnSavedSet ? mergeProposalWithExisting(sites, loadedSites) : proposalAsReplacement(sites);
    return {
      // Proposed rows first, then the saved sites none of them claims (build-on-top only): the
      // changes the user is reviewing sit at the top of the scrolling box, and the rest of the set
      // that the save will keep is still in view below them.
      rows: [...merge.rows.map(site => proposedToRow(site, proposalSource)), ...merge.keptExisting.map(keptToRow)],
      // What each row's status is judged against. Empty in replace mode, which shows no status.
      savedById: new Map((buildsOnSavedSet ? loadedSites : []).map(site => [site.id, site])),
      status: existing.status,
      savedSiteCount: loadedSites?.length ?? null,
      baselineSiteIds: savedSiteIdsAtProposal ?? loadedSites?.map(site => site.id) ?? null,
    };
  });
  const savedSiteCount = opened.savedSiteCount;
  // Build-on-top over a loaded set, which every row's status is judged against.
  const tracksSavedSet = mode === 'build-on-top' && opened.status === 'loaded';
  // A review with nothing proposed is the plain review of the identified sites. Every row would read
  // Unchanged, so there is no status column, and the note says what the sites are.
  const reviewsSavedSet = review && sites.length === 0;
  // A review opened again after a change holds the user's own sites too, so the set is described as
  // found only while every site in it came from the search.
  const reviewsFoundSites = opened.rows.every(row => row.source === 'identification-service');
  const showRowStatus = tracksSavedSet && !reviewsSavedSet;

  // The set the card will save, with every applied edit. The table edits a copy of it, as the page's
  // does: "Apply changes" writes the copy back, and "Cancel changes" drops it.
  const [rows, setRows] = useState<CardRow[]>(opened.rows);
  const editing = useEditingMode<CardRow>(rows, EMPTY_CARD_ROW);
  const shownRows = editing.isEditing ? editing.editedData : rows;
  // Set by a refused "Apply changes", as the page sets them on a refused save.
  const [validationMessages, setValidationMessages] = useState<string[]>([]);
  const [rowErrors, setRowErrors] = useState<RowErrors>({});

  const [commitState, setCommitState] = useState<CommitState>({ phase: 'idle' });
  // Set when the save endpoint refused the save because the set changed since the proposal, which is
  // how a save made from the page, the wizard or another tab reaches this card.
  const [changedAtSave, setChangedAtSave] = useState(false);
  // `_uiId` of the row being geocoded, else null. The geocode calls share one in-flight slot (like the
  // page) so only one row resolves at a time and the others' actions disable meanwhile.
  const [geocodingRowId, setGeocodingRowId] = useState<string | null>(null);
  // The latest geocode notice ("Approximate match for …"), as the page shows one above its table.
  const [geocodeNote, setGeocodeNote] = useState<string | null>(null);

  const saveMutation = useSaveSiteLocations({ companyId, variantId });
  const publishMutation = usePublishSiteLocations({ companyId, variantId });
  const geocodeMutation = useGeocodeSiteLocation({ companyId, variantId });
  // Before the first approval there is no variant: the company-scoped routes write the same draft.
  const saveDraftMutation = useSaveCompanySiteDraft(companyId);
  const geocodeDraftMutation = useGeocodeCompanySite(companyId);

  const pushGtmEvent = usePushGtmEvent();
  // "Edited": fire once, on the first applied edit.
  const editedFiredRef = useRef(false);
  // The approval goes to the chat as a message, as the product set card's does.
  const runtime = useAssistantRuntime();
  // Sending while the agent is still answering would cut that answer off.
  const isThreadRunning = useThread(state => state.isRunning);
  const cardRef = useRef<HTMLElement>(null);

  const isCommitting = commitState.phase === 'committing';
  const isCommitted = commitState.phase === 'committed';
  const isDismissed = commitState.phase === 'dismissed';
  // The table locks once a commit is in flight or done (including a save whose publish failed), and on dismiss.
  const locked = isCommitting || isCommitted || isDismissed || commitState.phase === 'publish-failed';
  const hasVariant = variantId !== null;
  // Where the table can be edited and saved at all.
  const canEdit = firstApproval || hasVariant;

  // The geocode action and the leading column are handed to the table, whose columns are memoised
  // on them; both have to keep their identity across renders, or every keystroke rebuilds the
  // columns and the cell being typed in loses focus.
  const { mutateAsync: geocodeAsync } = firstApproval ? geocodeDraftMutation : geocodeMutation;
  const { updateRow: updateEditedRow } = editing;
  const identifyCoordinates = useCallback(
    (row: CardRow) => {
      const uiId = row._uiId;
      if (!uiId) return;
      const label = row.name?.trim() || 'this site';
      setGeocodeNote(null);
      setGeocodingRowId(uiId);
      void geocodeAsync({
        streetAddress: row.streetAddress,
        addressLocality: row.addressLocality,
        addressRegion: row.addressRegion,
        postalCode: row.postalCode,
        addressCountry: row.addressCountry,
      })
        .then(result => {
          if ('dataAvailable' in result) {
            setGeocodeNote(`Couldn't find coordinates for ${label}. Try adding more address detail.`);
            return;
          }
          updateEditedRow(uiId, {
            latitude: result.latitude,
            longitude: result.longitude,
            ...geocodeVerdictOf(result),
          });
          // The page's two notices, for the same two reasons: Google may have approximated the
          // address, and an address that names a whole area is read as one grid cell inside it.
          // `extent` is optional-chained for the reason the page gives: a rolling deploy can pair
          // this bundle with an older backend that omits it.
          const notices = [
            result.partialMatch ? `Approximate match for ${label} — double-check the coordinates.` : null,
            result.extent?.caveat ?? null,
          ].filter((notice): notice is string => notice !== null);
          if (notices.length > 0) setGeocodeNote(notices.join(' '));
        })
        .catch(() => {
          setGeocodeNote('Could not reach the geocoding service. Please try again.');
        })
        .finally(() => {
          setGeocodingRowId(null);
        });
    },
    [geocodeAsync, updateEditedRow],
  );

  const hasSourceLinks = shownRows.some(row => row.evidenceUrl !== null && isHttpUrl(row.evidenceUrl));
  const { savedById } = opened;
  const leadingColumn = useMemo(
    () =>
      // Status in build-on-top, and the evidence link wherever a row has one, so a discovered site can
      // be checked rather than taken on trust. Without either there is nothing to say, and no column.
      showRowStatus || hasSourceLinks
        ? {
            header: showRowStatus ? 'Status' : 'Source',
            width: showRowStatus ? 176 : 80,
            cell: (row: CardRow) => (
              <RowStatusCell row={row} status={showRowStatus ? rowStatus(row, savedById) : null} />
            ),
          }
        : undefined,
    [showRowStatus, hasSourceLinks, savedById],
  );

  // The proposal itself has to pass the same checks: the tool schema does not hold a row to both
  // coordinates or neither.
  const rowsValidation = useMemo(() => validateRows(rows), [rows]);

  // Opens on the rows' own problems already marked, so a proposal that fails the checks shows which
  // cells to fix rather than waiting for a refused "Apply changes". The table's copy keeps each row's
  // `_uiId`, which is what the marks are keyed by.
  const startEditing = () => {
    setValidationMessages(rowsValidation.messages);
    setRowErrors(rowsValidation.rowErrors);
    editing.startEditing();
  };

  const cancelEditing = () => {
    setValidationMessages([]);
    setRowErrors({});
    setGeocodeNote(null);
    editing.cancelEditing();
  };

  // The page's save-time checks, run where this card's edit ends; the save itself comes later, from
  // the commit actions below.
  const applyEdits = () => {
    const result = validateRows(editing.editedData);
    setValidationMessages(result.messages);
    setRowErrors(result.rowErrors);
    if (!result.isValid) return;
    if (!editedFiredRef.current) {
      editedFiredRef.current = true;
      pushGtmEvent({ event: CustomGtmEvent.PLG_SITE_LOCATIONS_EDITED, data: funnelData });
    }
    setRows(editing.editedData);
    editing.exitEditing();
  };

  const rowsMissingCoordinates = shownRows.filter(row => !rowHasCoordinates(row));
  const coordinateLookupNote = coordinateLookup ? describeCoordinateLookup(coordinateLookup) : null;
  const rowsWithUnrecognisedCountry = shownRows.filter(row => !countryIsRecognised(row));
  // In build-on-top mode the commit is a transactional REPLACE of the whole draft with the table's
  // rows, kept saved sites included. Without the saved set loaded there is nothing to keep, so the
  // REPLACE would drop every saved site; the editor only opens after the load settles, so a failed
  // load is the case this closes. Replace mode commits the proposed rows whatever loaded.
  const existingSetReady = mode !== 'build-on-top' || opened.status === 'loaded';
  // Whether the set was saved after the proposal was made (`savedSetChangedSince`). Saving this card
  // would then undo that save, so it is refused in both modes: build-on-top would drop what the other
  // save added, and a replace would overwrite a set the user never saw. Only judged until this card's
  // own save lands in this mount — that save changes the ids too, and must not block its own publish
  // retry.
  const awaitingFirstSave = commitState.phase === 'idle' || commitState.phase === 'failed';
  // A replace proposal rebuilt without the baseline it was made against (made while the sites were
  // still being identified, or when that read failed) cannot tell whether it was saved already, and
  // saving it again would overwrite every site saved since. A build-on-top proposal is left to save:
  // it merges onto the set it loads, matching its rows to saved sites. Not every row matches, because
  // every save gives the sites new ids: a row the user edited before saving, and unnamed found sites
  // that share a city, come back marked New and would be added again. A known gap, limited to
  // proposals made before the first approval and visible as New rows before the user saves.
  const replayedWithoutBaseline = replayed && mode === 'replace' && savedSiteIdsAtProposal === null;
  const savedSetChanged =
    awaitingFirstSave &&
    (changedAtSave ||
      replayedWithoutBaseline ||
      (opened.baselineSiteIds !== null &&
        latestSavedSiteIds !== null &&
        savedSetChangedSince(opened.baselineSiteIds, latestSavedSiteIds)));
  // In build-on-top, a table that matches the saved set has nothing to save. Saving it anyway would
  // only recreate every site's id, which turns every other open card stale, and a publish would queue
  // a recompute with nothing to recompute. A card for a removal-only request opens like that, until
  // the user deletes the rows it was opened for.
  const appliedCounts = tracksSavedSet ? countStatuses(rows, savedById) : null;
  const changesSavedSet =
    appliedCounts === null || appliedCounts.edited + appliedCounts.added + appliedCounts.removed > 0;
  // Saving a draft needs: a resolvable variant, the existing set loaded (build-on-top only), an
  // unchanged saved set, no edit still open in the table, rows that pass the page's checks, and
  // (build-on-top) something to change. Missing coordinates deliberately do NOT block — such a row
  // saves fine and is only left out of the climate pathway, so it is flagged below instead.
  const canSave =
    hasVariant &&
    existingSetReady &&
    !savedSetChanged &&
    !editing.isEditing &&
    rowsValidation.isValid &&
    rows.length > 0 &&
    changesSavedSet;
  // Publishing runs the weight rules over the whole committed set, because that is what the publish
  // endpoint validates and refuses. Save stays available when they fail, so the user can keep the
  // work and fix the weights afterwards. Judged on the rows on screen, so a weight typed in the table
  // is answered before it is applied.
  const publishWeightProblems = useMemo(() => weightProblems(shownRows), [shownRows]);
  // A custom company's update is offered only to a user the publish endpoint would let make it.
  const canUpdateAssessment = customCompanyRecalculation?.allowed !== false;
  const canPublish = canSave && publishWeightProblems.length === 0 && canUpdateAssessment;
  // The review publishes these sites with the products, so approving it takes what publishing takes —
  // but no change, since the identified sites can be approved as they are, and no site, since a
  // company can have none.
  const canApprove =
    review &&
    existingSetReady &&
    !savedSetChanged &&
    !editing.isEditing &&
    rowsValidation.isValid &&
    publishWeightProblems.length === 0;
  // A proposal before the first approval (sites the user asked to add or change while the products
  // still await it) approves nothing: it saves to the company's draft, which the review then opens on.
  const canSaveToDraft =
    firstApproval &&
    !review &&
    existingSetReady &&
    !savedSetChanged &&
    !editing.isEditing &&
    rowsValidation.isValid &&
    changesSavedSet;

  // Saves the table to the company's draft before the first approval. False when it did not save, with
  // the card already showing why.
  const saveToCompanyDraft = async (): Promise<boolean> => {
    try {
      await saveDraftMutation.mutateAsync({
        sites: rows.map(rowToInput),
        ...(opened.baselineSiteIds !== null ? { expectedSiteIds: opened.baselineSiteIds } : {}),
      });
    } catch (err) {
      if (err instanceof ErrorWithHttpStatus && err.status === 409) {
        setChangedAtSave(true);
        setCommitState({ phase: 'idle' });
        return false;
      }
      setCommitState({
        phase: 'failed',
        message: err instanceof Error ? err.message : 'Failed to save site locations.',
      });
      return false;
    }
    pushGtmEvent({ event: CustomGtmEvent.PLG_SITE_LOCATIONS_SAVED, data: funnelData });
    return true;
  };

  // Save any change to the company's draft, then hand the approval to the chat, whose agent publishes
  // the products with these sites under its publish tool's checks.
  const approve = async () => {
    if (!canApprove || isThreadRunning || isCommitting || isCommitted) return;
    setCommitState({ phase: 'committing', published: false });
    if (changesSavedSet && !(await saveToCompanyDraft())) return;
    // As a card button that sends a message does (`McpAppCard`): follow the thread to the reply.
    cardRef.current?.closest('.aui-thread-viewport')?.dispatchEvent(new Event(FOLLOW_THREAD_EVENT));
    // The product set card's text for the same approval, so the agent answers both alike.
    runtime.thread.append(
      offerFinancialStatements ? `Looks good, add financials for ${subject}.` : 'Looks good, compute results.',
    );
    setCommitState({ phase: 'committed', published: false, recomputeJobId: null });
  };

  const saveProposalToCompanyDraft = async () => {
    if (!canSaveToDraft || isCommitting || isCommitted) return;
    setCommitState({ phase: 'committing', published: false });
    if (!(await saveToCompanyDraft())) return;
    setCommitState({ phase: 'committed', published: false, recomputeJobId: null });
  };

  // Save a draft, then optionally publish (which queues the recompute). Both actions route here so
  // the in-flight / committed / failed states are shared.
  const commit = async (published: boolean) => {
    // `canSave` already requires a resolved variant (`hasVariant`), so reaching here narrows
    // `variantId` to a non-null string — no separate null guard needed.
    if (!(published ? canPublish : canSave) || isCommitting || isCommitted) return;
    setCommitState({ phase: 'committing', published });
    let saved = false;
    try {
      // The committed set is one fully new geolocation input set: exactly the table's rows, which in
      // build-on-top include the kept saved sites. The page's own mapper, so a row saves the same way
      // from either surface. `expectedSiteIds` has the endpoint refuse the save, in the same
      // transaction as the write, if the set has been saved since the proposal was made — by the
      // page, the wizard, another tab, or this card in an earlier mount.
      await saveMutation.mutateAsync({
        sites: rows.map(rowToInput),
        ...(opened.baselineSiteIds !== null ? { expectedSiteIds: opened.baselineSiteIds } : {}),
      });
      saved = true;
      // "Saved": the draft was committed (fires for both the save-only and save+publish actions).
      pushGtmEvent({ event: CustomGtmEvent.PLG_SITE_LOCATIONS_SAVED, data: funnelData });
      if (published) {
        const { jobId } = await publishMutation.mutateAsync();
        // "Published": the draft was published and a recompute queued (the funnel's terminal step).
        pushGtmEvent({ event: CustomGtmEvent.PLG_SITE_LOCATIONS_PUBLISHED, data: funnelData });
        setCommitState({ phase: 'committed', published: true, recomputeJobId: jobId });
        return;
      }
      setCommitState({ phase: 'committed', published: false, recomputeJobId: null });
    } catch (err) {
      // A 409 on the save means the set changed under the proposal (or two first saves raced, which
      // amounts to the same): the card says so in its own words and turns saving off.
      if (!saved && err instanceof ErrorWithHttpStatus && err.status === 409) {
        setChangedAtSave(true);
        setCommitState({ phase: 'idle' });
        return;
      }
      const message = err instanceof Error ? err.message : 'Failed to save site locations.';
      setCommitState(saved ? { phase: 'publish-failed', message, retrying: false } : { phase: 'failed', message });
    }
  };

  // After a save landed and its publish failed, only the publish is retried: the draft is already the
  // set the user reviewed, and re-saving it would change nothing.
  const retryPublish = async () => {
    if (commitState.phase !== 'publish-failed' || commitState.retrying) return;
    setCommitState({ ...commitState, retrying: true });
    try {
      const { jobId } = await publishMutation.mutateAsync();
      pushGtmEvent({ event: CustomGtmEvent.PLG_SITE_LOCATIONS_PUBLISHED, data: funnelData });
      setCommitState({ phase: 'committed', published: true, recomputeJobId: jobId });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not update the assessment.';
      setCommitState({ phase: 'publish-failed', message, retrying: false });
    }
  };

  const dismiss = () => {
    if (isCommitting || isCommitted) return;
    setCommitState({ phase: 'dismissed' });
  };

  if (isCommitted) {
    if (firstApproval) {
      return review ? (
        <FirstApprovalReceipt subject={subject} outcome="approved" sites={rows} />
      ) : (
        <FirstApprovalReceipt
          subject={subject}
          outcome="saved"
          siteCount={rows.length}
          // A proposal saved after the review opened has just left that review on the old set.
          reviewAgain={canReviewAgain}
        />
      );
    }
    return (
      <SiteLocationsBatchReceipt
        subject={subject}
        published={commitState.published}
        count={rows.length}
        recomputeJobId={commitState.recomputeJobId}
        customCompanyRecalculation={customCompanyRecalculation}
      />
    );
  }

  if (commitState.phase === 'publish-failed') {
    return (
      <SiteLocationsBatchReceipt
        subject={subject}
        published={false}
        count={rows.length}
        publishFailure={{
          message: commitState.message,
          retrying: commitState.retrying,
          onRetry: () => {
            void retryPublish();
          },
        }}
      />
    );
  }

  if (isDismissed) {
    return (
      <section className="site-locations-batch-card" aria-label="Proposed site locations">
        <div className="tailoring-batch-card-hint">Proposal discarded.</div>
      </section>
    );
  }

  const statusCounts = showRowStatus ? countStatuses(shownRows, savedById) : null;

  return (
    <section
      ref={cardRef}
      className="site-locations-batch-card"
      aria-label={review ? 'Site locations to review' : 'Proposed site locations'}
    >
      <SiteLocationsBatchHeader
        subject={subject}
        siteCount={shownRows.length}
        mode={mode}
        savedSiteCount={savedSiteCount}
        review={review}
      />
      {summaryMarkdown && summaryMarkdown.length > 0 && (
        <div className="tailoring-batch-card-summary">
          <ChatMarkdown>{summaryMarkdown}</ChatMarkdown>
        </div>
      )}
      {savedSetChanged ? (
        <div className="site-locations-batch-note site-locations-batch-note--warning" role="alert">
          {review
            ? `The sites saved for ${subject} have changed since this review opened, so approving it could undo that change. Review the sites again to approve the set saved now.`
            : replayedWithoutBaseline
              ? canReviewAgain
                ? 'This proposal is from before the chat was reopened and may already have been saved. Saving it again could overwrite sites saved since, so review the sites again to approve the set saved now.'
                : 'This proposal is from before the chat was reopened and may already have been saved. Saving it again could overwrite sites saved since, so ask for a fresh proposal if you still want to change them.'
              : canReviewAgain
                ? 'The saved sites have changed since this proposal was made, possibly because it was already saved. Saving it again could undo those changes, so review the sites again to approve the set saved now.'
                : 'The saved sites have changed since this proposal was made, possibly because it was already saved. Saving it again could undo those changes, so ask for a fresh proposal if you still want to change them.'}
        </div>
      ) : reviewsSavedSet && opened.status === 'loaded' ? (
        <div className="site-locations-batch-note">
          {savedSiteCount
            ? `These are the sites ${reviewsFoundSites ? `found for ${subject} from public sources` : `saved for ${subject}`}. Check them before its results are calculated: edit, add or delete rows in “Edit table”.`
            : `No sites were found for ${subject}. Add any you know of in “Edit table”, or approve to continue without them.`}
        </div>
      ) : (
        <SiteLocationsBatchSetNote
          mode={mode}
          existingStatus={opened.status}
          savedSiteCount={savedSiteCount}
          statusCounts={statusCounts}
        />
      )}
      <div className="site-locations-batch-table-controls">
        <FormControlButtons
          isEditing={editing.isEditing}
          hasChanges={editing.hasChanges}
          onEdit={startEditing}
          onCancel={cancelEditing}
          onSave={applyEdits}
          disabled={locked || !canEdit}
          disabledReason={canEdit ? undefined : 'Open this chat from a company page to edit these sites.'}
          editButtonText="Edit table"
          // Nothing is saved here: the edit is applied to the proposal, which the commit actions save.
          saveButtonText="Apply changes"
          // A geocode answers into the table's copy, so applying before it lands would drop the result.
          saveDisabled={geocodingRowId !== null}
          addButtonConfig={{ onAdd: editing.addRow, label: 'Add site' }}
        />
      </div>
      <SiteLocationsTable<CardRow>
        data={shownRows}
        isEditing={editing.isEditing}
        isLoading={false}
        onUpdateRow={editing.updateRow}
        onDeleteRow={editing.deleteRow}
        onIdentifyFromAddress={identifyCoordinates}
        geocodingRowId={geocodingRowId}
        rowErrors={rowErrors}
        // Every site in one scrolling box, as in the wizard's review step: a proposal is reviewed before
        // anything is saved, so no site should wait on a second page.
        layout="review-step"
        leadingColumn={leadingColumn}
      />
      {editing.isEditing &&
        validationMessages.map(message => (
          <div key={message} className="tailoring-batch-card-error" role="alert">
            {message}
          </div>
        ))}
      {geocodeNote && <div className="tailoring-batch-card-hint">{geocodeNote}</div>}
      {savedSetChanged && canReviewAgain ? (
        <ReviewSitesAgainAction subject={subject} />
      ) : review ? (
        <FirstApprovalActions
          kind="review"
          offerFinancialStatements={offerFinancialStatements}
          subject={subject}
          canApprove={canApprove && !isThreadRunning}
          isCommitting={isCommitting}
          onApprove={() => {
            void approve();
          }}
        />
      ) : firstApproval ? (
        <FirstApprovalActions
          kind="proposal"
          subject={subject}
          canSave={canSaveToDraft}
          isCommitting={isCommitting}
          onSave={() => {
            void saveProposalToCompanyDraft();
          }}
          onDismiss={dismiss}
        />
      ) : (
        <SiteLocationsBatchActions
          subject={subject}
          canSave={canSave}
          canPublish={canPublish}
          canUpdateAssessment={canUpdateAssessment}
          committingPublished={isCommitting ? commitState.published : null}
          onSave={() => {
            void commit(false);
          }}
          onSaveAndPublish={() => {
            void commit(true);
          }}
          onDismiss={dismiss}
        />
      )}
      {commitState.phase === 'failed' && (
        <div className="tailoring-batch-card-error" role="alert">
          {commitState.message}
        </div>
      )}
      {!canEdit && (
        <div className="tailoring-batch-card-hint">
          Open this chat from a company page to save the proposed site locations.
        </div>
      )}
      {canEdit && editing.isEditing && (
        <div className="tailoring-batch-card-hint">
          {review
            ? 'Apply or cancel your changes to the table before approving.'
            : 'Apply or cancel your changes to the table before saving.'}
        </div>
      )}
      {canEdit && !review && !editing.isEditing && !savedSetChanged && !changesSavedSet && (
        <div className="tailoring-batch-card-hint">
          The table matches the saved sites, so there is nothing to save yet. Use &ldquo;Edit table&rdquo; to change,
          add or delete sites.
        </div>
      )}
      {canEdit &&
        !editing.isEditing &&
        rowsValidation.messages.map(message => (
          <div key={message} className="tailoring-batch-card-hint">
            {message} Edit the table to fix it before {review ? 'approving' : 'saving'}.
          </div>
        ))}
      {coordinateLookupNote && <div className="tailoring-batch-card-hint">{coordinateLookupNote}</div>}
      {canEdit && rowsMissingCoordinates.length > 0 && (
        <div className="tailoring-batch-card-hint">
          {rowsMissingCoordinates.length === 1 ? '1 site has ' : `${rowsMissingCoordinates.length} sites have `}
          no coordinates. They still save, but stay out of the physical climate-risk assessment until coordinates are
          added — in &ldquo;Edit table&rdquo;, identify them from the address or type them in.
        </div>
      )}
      {canEdit && rowsWithUnrecognisedCountry.length > 0 && (
        <div className="tailoring-batch-card-hint">
          {rowsWithUnrecognisedCountry.length === 1 ? '1 site has' : `${rowsWithUnrecognisedCountry.length} sites have`}{' '}
          a country that isn&apos;t on the platform&apos;s list:{' '}
          {describeUnrecognisedCountries(rowsWithUnrecognisedCountry)}.{' '}
          {rowsWithUnrecognisedCountry.length === 1
            ? 'It still saves. To fix it, pick its country'
            : 'They still save. To fix one, pick its country'}{' '}
          from the list in &ldquo;Edit table&rdquo;.
        </div>
      )}
      {canEdit &&
        publishWeightProblems.map(problem => (
          <div key={problem} className="tailoring-batch-card-hint">
            {problem} {review ? 'Fix it in “Edit table” before approving.' : 'You can still save this as a draft.'}
          </div>
        ))}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sub-components — header, set note, status cell, actions, receipt. Private.
// ---------------------------------------------------------------------------

function SiteLocationsBatchHeader({
  subject,
  siteCount,
  mode,
  savedSiteCount,
  review = false,
}: {
  subject: string;
  /** How many sites the table holds, or null while the saved set loads and that is not known yet. */
  siteCount: number | null;
  mode: SiteLocationsBatchMode;
  /** How many sites are saved, or null while that is unknown. */
  savedSiteCount: number | null;
  /** The site review before a custom company's first results, which proposes nothing of its own. */
  review?: boolean;
}) {
  // Subject first, then the count and mode, joined the way the MCP cards' `CardSubtitle` joins them.
  // With nothing saved there is no set to replace or build on, so the mode is left out. A review is
  // of the saved set itself, which its note describes.
  const items = [subject];
  if (siteCount !== null) items.push(siteCount === 1 ? '1 site' : `${siteCount} sites`);
  if (savedSiteCount !== 0 && !review) {
    items.push(mode === 'build-on-top' ? 'building on the saved set' : 'replacing the saved set');
  }
  return (
    <header className="tailoring-batch-card-header">
      <div className="tailoring-batch-card-title">{review ? 'Review site locations' : 'Proposed site locations'}</div>
      <div className="tailoring-batch-card-subtitle">{items.join(' · ')}</div>
    </header>
  );
}

interface StatusCounts {
  edited: number;
  added: number;
  unchanged: number;
  /** Saved sites with no row left in the table, which the save therefore drops. */
  removed: number;
}

function countStatuses(rows: CardRow[], savedById: ReadonlyMap<number, CompanyGeolocationSite>): StatusCounts {
  const counts: StatusCounts = { edited: 0, added: 0, unchanged: 0, removed: 0 };
  const stillThere = new Set<number>();
  for (const row of rows) {
    if (row.matchedExistingId !== null) stillThere.add(row.matchedExistingId);
    const status = rowStatus(row, savedById);
    if (status === 'edited') counts.edited += 1;
    else if (status === 'unchanged') counts.unchanged += 1;
    else counts.added += 1;
  }
  counts.removed = [...savedById.keys()].filter(id => !stillThere.has(id)).length;
  return counts;
}

/**
 * What saving does to the company's saved sites, said before the user presses Save: in build-on-top
 * how the table differs from the saved set, in replace mode that the saved set is about to be
 * overwritten. A replace with no warning was how a model's wrong mode choice could silently discard
 * a company's whole site list.
 */
function SiteLocationsBatchSetNote({
  mode,
  existingStatus,
  savedSiteCount,
  statusCounts,
}: {
  mode: SiteLocationsBatchMode;
  existingStatus: ExistingSetState['status'];
  savedSiteCount: number | null;
  /** Build-on-top with the saved set loaded; null otherwise. */
  statusCounts: StatusCounts | null;
}) {
  if (existingStatus === 'unavailable') return null;
  const sites = (n: number) => (n === 1 ? '1 site' : `${n} sites`);

  if (existingStatus === 'failed') {
    return (
      <div className="site-locations-batch-note site-locations-batch-note--warning" role="alert">
        {mode === 'build-on-top'
          ? "Couldn't load the sites already saved for this company, so these rows can't be combined with them. Saving is off until they load — try again in a moment."
          : "Couldn't check whether this company already has saved sites. Saving replaces any that exist."}
      </div>
    );
  }

  if (mode === 'replace' || statusCounts === null) {
    if (!savedSiteCount) return null;
    return (
      <div className="site-locations-batch-note site-locations-batch-note--warning" role="alert">
        Saving replaces the {sites(savedSiteCount)} currently saved for this company with the rows below.
      </div>
    );
  }

  if (!savedSiteCount) {
    return (
      <div className="site-locations-batch-note">This company has no saved sites yet, so every row below is new.</div>
    );
  }
  const { edited, added, unchanged, removed } = statusCounts;
  const parts = [`${edited} edited`, `${added} new`, `${unchanged} unchanged`];
  if (removed > 0) parts.push(`${removed} removed`);
  return (
    <div
      className={`site-locations-batch-note${removed > 0 ? ' site-locations-batch-note--warning' : ''}`}
      role={removed > 0 ? 'alert' : undefined}
    >
      The table is the whole set that will be saved, built on the {sites(savedSiteCount)} saved now: {parts.join(', ')}.
    </div>
  );
}

function RowStatusCell({ row, status }: { row: CardRow; status: RowStatus | null }) {
  const badge = status === null ? null : ROW_STATUS_BADGES[status];
  const evidenceUrl = row.evidenceUrl?.trim() ?? '';
  return (
    <span className="site-locations-batch-status">
      {badge && (
        <span title={badge.explanation}>
          <Badge tone={badge.tone}>{badge.label}</Badge>
        </span>
      )}
      {/* The evidence for a discovered site, so the reviewer can check it rather than take it on
          trust. Only an http(s) URL becomes a link: the value is agent- or upload-supplied text. */}
      {isHttpUrl(evidenceUrl) && (
        <a className="site-locations-batch-row-source" href={evidenceUrl} target="_blank" rel="noopener noreferrer">
          Source
        </a>
      )}
    </span>
  );
}

function SiteLocationsBatchActions({
  subject,
  canSave,
  canPublish,
  canUpdateAssessment,
  committingPublished,
  onSave,
  onSaveAndPublish,
  onDismiss,
}: {
  /** The company's name, or its id when no name was resolved. */
  subject: string;
  canSave: boolean;
  /** Stricter than `canSave`: publish also has to satisfy the weight rules. */
  canPublish: boolean;
  /** False when this user may not update the assessment at all: only the draft is offered then. */
  canUpdateAssessment: boolean;
  /** null when not committing; otherwise which action is in flight (true = save + publish). */
  committingPublished: boolean | null;
  onSave: () => void;
  onSaveAndPublish: () => void;
  onDismiss: () => void;
}) {
  const isCommitting = committingPublished !== null;
  return (
    <>
      {/* Said before the click, not after it (DESIGN.md, "Commitment points"): which of the two
          commits leaves the assessment alone, and which one publishes and starts a recalculation. */}
      <div className="tailoring-batch-card-hint">
        {canUpdateAssessment ? (
          <>
            Save as draft keeps the assessment as it is. Save and update assessment publishes these sites and
            recalculates the company&apos;s climate assessment.
          </>
        ) : (
          `Your account can save these sites as a draft, but can't update the assessment of ${subject} with them.`
        )}
      </div>
      {/* Secondary actions first and the one primary last, in DOM and visual order (DESIGN.md, "Where
          the action row goes"). Without the update, saving the draft is the primary. */}
      <div className="tailoring-batch-card-actions">
        <button
          type="button"
          className="tailoring-batch-card-action tailoring-batch-card-action--secondary"
          disabled={isCommitting}
          onClick={onDismiss}
        >
          Discard proposal
        </button>
        <button
          type="button"
          className={`tailoring-batch-card-action tailoring-batch-card-action--${canUpdateAssessment ? 'secondary' : 'primary'}`}
          disabled={!canSave || isCommitting}
          onClick={onSave}
        >
          {committingPublished === false ? 'Saving…' : 'Save as draft'}
        </button>
        {canUpdateAssessment && (
          <button
            type="button"
            className="tailoring-batch-card-action tailoring-batch-card-action--primary"
            disabled={!canPublish || isCommitting}
            onClick={onSaveAndPublish}
          >
            {committingPublished === true ? 'Saving and updating…' : 'Save and update assessment'}
          </button>
        )}
      </div>
    </>
  );
}

/**
 * The actions before a custom company's first results (PLT-6999). What each commits is said before
 * the click (DESIGN.md, "Commitment points"). The review approves: the sites are saved, and the
 * company's first results are calculated from them and its products. A proposal made while the
 * products still await approval only saves, and the review that follows the products opens on it.
 */
function FirstApprovalActions(
  props: { subject: string; isCommitting: boolean; offerFinancialStatements?: boolean } & (
    | { kind: 'review'; canApprove: boolean; onApprove: () => void }
    | { kind: 'proposal'; canSave: boolean; onSave: () => void; onDismiss: () => void }
  ),
) {
  const { subject, isCommitting } = props;
  if (props.kind === 'review') {
    return (
      <>
        <div className="tailoring-batch-card-hint">
          {props.offerFinancialStatements
            ? "Approving saves these sites. You can then add the company's financials before its results are calculated."
            : `Approving saves these sites and calculates the results for ${subject} from them and its products.`}
        </div>
        <div className="tailoring-batch-card-actions">
          <button
            type="button"
            className="tailoring-batch-card-action tailoring-batch-card-action--primary"
            disabled={!props.canApprove || isCommitting}
            onClick={props.onApprove}
          >
            {isCommitting
              ? 'Saving…'
              : props.offerFinancialStatements
                ? 'Looks good, add financials'
                : 'Looks good, compute results'}
          </button>
        </div>
      </>
    );
  }
  return (
    <>
      <div className="tailoring-batch-card-hint">
        Saving keeps these sites for {subject} until its products are approved; nothing is calculated before then.
      </div>
      <div className="tailoring-batch-card-actions">
        <button
          type="button"
          className="tailoring-batch-card-action tailoring-batch-card-action--secondary"
          disabled={isCommitting}
          onClick={props.onDismiss}
        >
          Discard proposal
        </button>
        <button
          type="button"
          className="tailoring-batch-card-action tailoring-batch-card-action--primary"
          disabled={!props.canSave || isCommitting}
          onClick={props.onSave}
        >
          {isCommitting ? 'Saving…' : 'Save sites'}
        </button>
      </div>
    </>
  );
}

/**
 * The way on from a card whose saved set changed under it, before a custom company's first results
 * (PLT-7368). It sends what the products card sends to open the review, so the agent answers with a
 * fresh review of the set saved now, which replaces this one. Nothing is saved or calculated by it.
 */
function ReviewSitesAgainAction({ subject }: { subject: string }) {
  const runtime = useAssistantRuntime();
  // Sending while the agent is still answering would cut that answer off.
  const isThreadRunning = useThread(state => state.isRunning);
  const actionsRef = useRef<HTMLDivElement>(null);
  return (
    <>
      <div className="tailoring-batch-card-hint">
        This opens a fresh review of the sites saved for {subject} now, to check and approve with its products.
      </div>
      <div ref={actionsRef} className="tailoring-batch-card-actions">
        <button
          type="button"
          className="tailoring-batch-card-action tailoring-batch-card-action--primary"
          disabled={isThreadRunning}
          onClick={() => {
            // As a card button that sends a message does (`McpAppCard`): follow the thread to the reply.
            actionsRef.current?.closest('.aui-thread-viewport')?.dispatchEvent(new Event(FOLLOW_THREAD_EVENT));
            runtime.thread.append('Looks good, review sites.');
          }}
        >
          Review sites again
        </button>
      </div>
    </>
  );
}

/**
 * PLT-7453: a review the user approved, in a later mount or once its publish has followed, listing the
 * sites the publish recorded as approved. A publish from before publishes recorded them leaves the
 * company's sites to read instead: its published set, which its results are calculated from, or the
 * draft while the approval is still publishing it.
 */
function ApprovedSitesReceipt({
  companyId,
  subject,
  approvedSites,
}: {
  companyId: string;
  subject: string;
  approvedSites: CompanyGeolocationSiteValues[] | undefined;
}) {
  const savedQuery = useCompanySiteDraft(approvedSites === undefined ? companyId : undefined);
  const sites = useMemo(() => {
    if (approvedSites !== undefined) return approvedSites.map(siteToRow);
    if (savedQuery.data === undefined) return null;
    return ((savedQuery.data.published ?? savedQuery.data.draft)?.sites ?? []).map(siteToRow);
  }, [approvedSites, savedQuery.data]);
  return <FirstApprovalReceipt subject={subject} outcome="approved" sites={sites} />;
}

const NO_ROW_ERRORS: Partial<Record<string, Set<keyof SiteRow>>> = {};
const ignoreRowEdit = () => undefined;

/**
 * Before a custom company's first results: a review the user approved, listing its `sites` (null when
 * they cannot be read) in full, as an approved product set lists its products, or a proposal saved for
 * the review to come. `reviewAgain` when that save left an open review on the old set.
 */
function FirstApprovalReceipt(
  props: { subject: string } & (
    | { outcome: 'approved'; sites: SiteRow[] | null }
    | { outcome: 'saved'; siteCount: number; reviewAgain?: boolean }
  ),
) {
  const { subject } = props;
  const siteCount = props.outcome === 'approved' ? (props.sites?.length ?? null) : props.siteCount;
  const sites = siteCount === 1 ? '1 site' : `${siteCount} sites`;
  const line =
    props.outcome === 'saved'
      ? `${sites} saved for ${subject}. They are reviewed with its products before its results are calculated.`
      : siteCount === null
        ? `The results for ${subject} are calculated from these sites and its products.`
        : siteCount === 0
          ? `No sites for ${subject}: its results are calculated from its products alone.`
          : `${sites} saved for ${subject}. Its results are calculated from them and its products.`;
  const listedSites =
    props.outcome === 'approved' && props.sites !== null && props.sites.length > 0 ? props.sites : null;
  return (
    // A card that lists the sites is a white card like the table it replaced; a line alone is a receipt.
    <section
      className={listedSites ? 'site-locations-batch-card' : 'site-locations-batch-card tailoring-batch-card--receipt'}
      aria-label="Saved site locations"
    >
      <header className="tailoring-batch-card-header">
        <div className="tailoring-batch-card-title">
          {props.outcome === 'saved' ? 'Site locations saved' : 'Site locations approved'}
        </div>
        <div className="tailoring-batch-card-subtitle">{line}</div>
      </header>
      {listedSites && (
        <SiteLocationsTable
          data={listedSites}
          isEditing={false}
          isLoading={false}
          onUpdateRow={ignoreRowEdit}
          onDeleteRow={ignoreRowEdit}
          onIdentifyFromAddress={ignoreRowEdit}
          geocodingRowId={null}
          rowErrors={NO_ROW_ERRORS}
          layout="review-step"
        />
      )}
      {props.outcome === 'saved' && props.reviewAgain && <ReviewSitesAgainAction subject={subject} />}
    </section>
  );
}

function SiteLocationsBatchReceipt({
  subject,
  published,
  count,
  recomputeJobId = null,
  publishFailure,
  customCompanyRecalculation,
}: {
  /** The company's name, or its id when no name was resolved. */
  subject: string;
  published: boolean;
  count: number;
  /** The recalculation publish queued, polled so the receipt can say when it finishes. */
  recomputeJobId?: number | null;
  /** Set when the draft saved but updating the assessment did not; the draft stays saved. */
  publishFailure?: { message: string; retrying: boolean; onRetry: () => void };
  /** For a custom company, which has no Site locations page: its sites are recalculated elsewhere. */
  customCompanyRecalculation?: { allowed: boolean };
}) {
  const siteLabel = count === 1 ? '1 site' : `${count} sites`;
  // Where a draft goes next. A custom company has no Site locations page: its sites are recalculated
  // from its edit dialog's Site locations tab, reached from its own page or from the list it is on.
  const draftLine =
    customCompanyRecalculation === undefined
      ? `${siteLabel} saved as a draft. Update the assessment from the Site locations page when you are ready to recompute.`
      : customCompanyRecalculation.allowed
        ? `${siteLabel} saved as a draft. To include them in the results, edit the site locations of ${subject} on the platform and choose “Save and recalculate”.`
        : `${siteLabel} saved as a draft. Your account can't update the assessment of ${subject}, so they stay out of its results until someone who can recalculates it.`;
  // Polled the way the tailoring-removal card polls its recompute: until the job completes. A save
  // without publish queues nothing, so there is nothing to poll.
  const recomputeStatus = useFetchJobStatus(recomputeJobId ?? undefined);
  // A status read that fails stops the polling for good, so the receipt stops claiming to follow the
  // job and says only what is known: the recalculation was queued.
  const recompute: 'none' | 'running' | 'done' | 'failed' =
    !published || recomputeJobId === null || recomputeStatus.isError
      ? 'none'
      : recomputeStatus.data?.status === JobStatus.ERROR
        ? 'failed'
        : recomputeStatus.data && getIsJobComplete(recomputeStatus.data.status)
          ? 'done'
          : 'running';
  // Once the recalculation succeeds, refresh the results queries so a climate view open in this tab
  // shows the new numbers, as the Site locations page does after its own publish. Once only, and not
  // on failure: nothing was rewritten then.
  const queryClient = useQueryClient();
  const hasRefreshedResults = useRef(false);
  useEffect(() => {
    if (recomputeStatus.data?.status !== JobStatus.SUCCESS || hasRefreshedResults.current) return;
    hasRefreshedResults.current = true;
    void queryClient.invalidateQueries({ queryKey: ['materiality'] });
  }, [recomputeStatus.data, queryClient]);
  const publishedTitle = {
    none: 'Site locations saved and published',
    running: 'Site locations saved — updating assessment',
    done: 'Site locations saved — assessment updated',
    failed: 'Site locations saved — assessment update failed',
  }[recompute];
  const publishedLine = {
    // No job id back from publish: the recalculation was queued without one to follow.
    none: `${siteLabel} published. The climate assessment will recalculate with them.`,
    running: `${siteLabel} published. Recalculating the climate assessment with them…`,
    done: `${siteLabel} published. The climate assessment now uses them.`,
    failed: `${siteLabel} published, but recalculating the climate assessment failed. The sites stay saved, and the next recalculation uses them.`,
  }[recompute];
  return (
    <section className="site-locations-batch-card tailoring-batch-card--receipt" aria-label="Saved site locations">
      <header className="tailoring-batch-card-header">
        <div className="tailoring-batch-card-title">{published ? publishedTitle : 'Site locations saved as draft'}</div>
        <div className="tailoring-batch-card-subtitle" aria-live="polite">
          {published
            ? publishedLine
            : publishFailure
              ? `${siteLabel} saved as a draft, but the assessment was not updated.`
              : draftLine}
        </div>
      </header>
      {publishFailure && (
        <>
          <div className="tailoring-batch-card-error" role="alert">
            {publishFailure.message}
          </div>
          <div className="tailoring-batch-card-actions">
            <button
              type="button"
              className="tailoring-batch-card-action tailoring-batch-card-action--primary"
              disabled={publishFailure.retrying}
              onClick={publishFailure.onRetry}
            >
              {publishFailure.retrying ? 'Updating…' : 'Try updating the assessment again'}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
